/**
 * The addon library: ready-made addons you can install from Settings.
 *
 * Empty on purpose. The previous fifteen were written before the theme
 * tokens reached the sandbox properly, so several of them looked wrong in
 * the app they were meant to belong to, and they were cleared out rather
 * than patched one at a time.
 *
 * What survives is the part worth keeping: KIT, the small stylesheet every
 * addon shares, and the shape of an entry. Adding one back is appending an
 * object to ADDON_LIBRARY below with `code: KIT + YOUR_BODY`.
 *
 * Each addon is an ordinary addon - the same sandbox, the same small API,
 * no network and no access to your account - so they double as working
 * examples of what an addon can do.
 *
 * Written as plain ES5 strings because they run inside the sandbox page
 * as-is. No template literals in addon code: String.raw would still read
 * ${...}.
 */

/* ------------------------------------------------------------------ kit */

const KIT = String.raw`
var KIT = (function () {
  var css = document.createElement('style');
  css.textContent =
    'body{padding:12px 12px 14px;font-size:13px}' +
    '*{scrollbar-width:thin}' +
    '.row{display:flex;align-items:center;gap:8px;flex-wrap:wrap}' +
    '.col{display:flex;flex-direction:column;gap:8px}' +
    '.grow{flex:1;min-width:0}' +
    '.card{background:var(--bg-2);border:1px solid var(--line);border-radius:12px;padding:10px 12px}' +
    '.muted{color:var(--text-mute)}.dim{color:var(--text-dim)}' +
    '.label{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--text-mute);margin-bottom:4px}' +
    '.big{font-size:26px;font-weight:800;letter-spacing:-.02em;line-height:1.1}' +
    '.mid{font-size:16px;font-weight:700}' +
    'input,select,textarea{font:inherit;color:var(--text);background:var(--bg-1);border:1px solid var(--line);border-radius:9px;padding:7px 9px;outline:none;width:100%;box-sizing:border-box}' +
    'textarea{resize:vertical;min-height:70px}' +
    'input:focus,select:focus,textarea:focus{border-color:var(--accent)}' +
    'button{border-radius:9px;padding:7px 12px;font-weight:600}' +
    'button.pri{background:var(--accent);color:#fff;border-color:transparent}' +
    'button.pri:hover{background:var(--accent-hi)}' +
    'button.ghost{background:transparent;border-color:transparent;color:var(--text-dim)}' +
    'button.ghost:hover{background:var(--bg-3);color:var(--text)}' +
    'button.bad{color:var(--bad)}' +
    'button:disabled{opacity:.45;cursor:default}' +
    '.chip{display:inline-flex;align-items:center;gap:4px;border-radius:999px;padding:3px 9px;font-size:11.5px;font-weight:700;background:var(--bg-3);color:var(--text-dim);border:1px solid transparent;cursor:pointer}' +
    '.chip.on{background:var(--accent);color:#fff}' +
    /* The waiting-room games: a question worth reading across the room, and
       four answers big enough to hit on a television. */
    '.q{margin:0;font-size:18px;font-weight:700;line-height:1.3}' +
    '.answer{display:block;width:100%;text-align:left;padding:10px 12px;border-radius:10px;background:var(--bg-2);border:1px solid var(--line);font-size:14px;font-weight:600}' +
    '.answer.picked{border-color:var(--accent)}' +
    '.answer.right{background:color-mix(in srgb,var(--good) 22%,var(--bg-2));border-color:var(--good)}' +
    '.answer.wrong{background:color-mix(in srgb,var(--bad) 18%,var(--bg-2));border-color:var(--bad)}' +
    '.tabs{display:flex;gap:4px;background:var(--bg-2);border-radius:10px;padding:3px;margin-bottom:10px}' +
    '.tabs button{flex:1;border:0;background:transparent;padding:6px 8px;font-size:12.5px}' +
    '.tabs button.on{background:var(--accent);color:#fff}' +
    '.good{color:var(--good)}.bad-t{color:var(--bad)}.warn{color:var(--warn)}' +
    '.grid2{display:grid;grid-template-columns:1fr 1fr;gap:8px}' +
    '.grid3{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}' +
    '.mono{font-family:ui-monospace,SFMono-Regular,Consolas,monospace;font-size:12px;word-break:break-all;white-space:pre-wrap}' +
    '.stat{background:var(--bg-2);border-radius:10px;padding:8px 10px;text-align:center}' +
    '.stat b{display:block;font-size:18px}' +
    '.stat span{font-size:11px;color:var(--text-mute)}' +
    '.pbar{display:flex;align-items:center;gap:7px;font-size:11.5px;color:var(--text-dim);background:var(--bg-2);border:1px solid var(--line);border-radius:9px;padding:5px 9px;margin-bottom:9px}' +
    '.pbar i{width:7px;height:7px;border-radius:9px;background:var(--good);flex:none;box-shadow:0 0 0 3px rgba(63,178,127,.18)}' +
    'body.ro [data-act]:not([data-free]){pointer-events:none}' +
    'body.ro .edit-only{opacity:.5;filter:saturate(.55)}' +
    'body.ro .edit-only,body.ro .edit-only *{cursor:not-allowed}' +
    'body.ro input:not([data-free]),body.ro textarea:not([data-free]),body.ro select:not([data-free]){pointer-events:none;opacity:.75}' +
    '.me-row{background:color-mix(in srgb,var(--accent) 12%,var(--bg-2))}';
  document.head.appendChild(css);

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  var fitTimer = 0;
  function fit() {
    clearTimeout(fitTimer);
    fitTimer = setTimeout(function () {
      pugo.setHeight(Math.min(600, document.documentElement.scrollHeight + 2));
    }, 30);
  }
  function load(defaults) {
    return pugo.storage.get().then(function (saved) {
      var out = {};
      for (var k in defaults) out[k] = defaults[k];
      if (saved) for (var j in saved) out[j] = saved[j];
      return out;
    });
  }
  var saveTimer = 0;
  function save(state) {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(function () { pugo.storage.set(state); }, 150);
  }
  function copy(text) {
    if (pugo.copy) return pugo.copy(text).then(function (ok) { return !!ok; });
    return new Promise(function (resolve) {
      function fallback() {
        var ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        var ok = false;
        try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
        document.body.removeChild(ta);
        resolve(ok);
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(function () { resolve(true); }, fallback);
        } else fallback();
      } catch (e) { fallback(); }
    });
  }
  function on(root, type, fn) {
    root.addEventListener(type, function (e) {
      var el = e.target.closest ? e.target.closest('[data-act]') : null;
      if (!el || !root.contains(el)) return;
      /* Read-only in a party: only the controls marked free still work. */
      if (document.body.classList.contains('ro') && !el.hasAttribute('data-free')) return;
      fn(el.getAttribute('data-act'), el, e);
    });
  }

  /* ---- parties ----
     In a party an addon can keep its state in the party instead of on this
     machine: the owner and officers write it, everybody sees it live. */
  var PARTY = (function () {
    var ctx = null, sent = [], shareTimer = 0, mineTimer = 0, pending = null, listeners = [];
    function init() {
      if (!pugo.party) return Promise.resolve(null);
      return pugo.party.get().then(function (c) {
        ctx = c || null;
        if (!ctx) return null;
        pugo.on('party', function (next) {
          var prev = ctx;
          ctx = next;
          /* Our own writes come back too; those are not news. */
          var incoming = JSON.stringify(next.shared || {});
          var echo = sent.indexOf(incoming) !== -1;
          listeners.forEach(function (fn) { try { fn(next, !echo, prev); } catch (err) {} });
        });
        return ctx;
      });
    }
    function share(data) {
      if (!ctx || !ctx.canEdit) return;
      var json = JSON.stringify(data);
      sent.push(json);
      if (sent.length > 30) sent.shift();
      pending = JSON.parse(json);
      clearTimeout(shareTimer);
      shareTimer = setTimeout(function () { pugo.party.setShared(pending); }, 200);
    }
    function mine(data) {
      if (!ctx) return;
      var copyOf = data === null ? null : JSON.parse(JSON.stringify(data));
      clearTimeout(mineTimer);
      mineTimer = setTimeout(function () { pugo.party.setMine(copyOf); }, 200);
    }
    return {
      init: init, share: share, mine: mine,
      on: function (fn) { listeners.push(fn); },
      get: function () { return ctx; }
    };
  })();

  function fill(target, defaults, src) {
    for (var k in target) delete target[k];
    var base = JSON.parse(JSON.stringify(defaults));
    for (var a in base) target[a] = base[a];
    if (src) for (var b in src) target[b] = src[b];
  }

  /*
   * Starts an addon that works on its own and in a party.
   * run(s, P): s is the state - saved on this machine, or the party's copy -
   * and P says where it runs: P.ctx (null on Home), P.canEdit, P.save(),
   * P.onRemote(fn) for changes made by somebody else.
   */
  function start(defaults, run) {
    load(defaults).then(function (local) {
      PARTY.init().then(function (ctx) {
        var st = local;
        if (ctx) { st = {}; fill(st, defaults, ctx.shared); }
        var remote = [];
        var P = {
          ctx: ctx,
          canEdit: !ctx || !!ctx.canEdit,
          save: function () { if (P.ctx) PARTY.share(st); else save(st); },
          onRemote: function (fn) { remote.push(fn); }
        };
        if (ctx) {
          PARTY.on(function (next, fromOthers, prev) {
            P.ctx = next;
            P.canEdit = !!next.canEdit;
            if (fromOthers) fill(st, defaults, next.shared);
            remote.forEach(function (fn) { fn(next, fromOthers, prev); });
          });
        }
        run(st, P);
      });
    });
  }

  /* Redraw without losing the field somebody is typing in. */
  function redraw(draw) {
    var a = document.activeElement, key = null, attr = null, from = 0, to = 0;
    if (a && a !== document.body && pugo.root.contains(a)) {
      ['data-f', 'data-k', 'data-e', 'data-act-input'].forEach(function (n) { if (!attr && a.getAttribute(n)) { attr = n; key = a.getAttribute(n); } });
      try { from = a.selectionStart; to = a.selectionEnd; } catch (err) {}
    }
    draw();
    if (attr) {
      var el = pugo.root.querySelector('[' + attr + '="' + key + '"]');
      if (el) { el.focus(); try { el.setSelectionRange(from, to); } catch (err) {} }
    }
  }
  function ro(P) { document.body.classList.toggle('ro', !!P && !P.canEdit); }
  function partyBar(P) {
    if (!P || !P.ctx) return '';
    return '<div class="pbar"><i></i><span>' + (P.canEdit ? 'Live in your party - everyone sees what you do here' : 'Live - the host has locked this one, so only they can change it') + '</span></div>';
  }
  function memberName(P, id) {
    var list = (P && P.ctx && P.ctx.members) || [];
    for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i].name;
    return 'Player';
  }
  function uid() { return Math.random().toString(36).slice(2, 9); }
  return {
    esc: esc, fit: fit, load: load, save: save, copy: copy, on: on, uid: uid,
    start: start, redraw: redraw, ro: ro, partyBar: partyBar, party: PARTY, memberName: memberName
  };
})();
`;

export type LibraryAddon = {
  id: string;
  name: string;
  icon: string;
  tagline: string;
  description: string;
  code: string;
  /** Can run inside a party, live for everyone in it. */
  party?: boolean;
  /** What it does in a party, for the party's add list. */
  partyNote?: string;
};

/*
 * Nothing yet.
 *
 * KIT above is referenced here so the kit is not dropped as dead code while
 * the shelf is empty - the next entry needs it, and a build that had
 * quietly removed it would be a confusing thing to walk back into.
 */
export const ADDON_LIBRARY: LibraryAddon[] = [];

/** Kept alive for the first entry that needs it. */
export const ADDON_KIT = KIT;

export const PARTY_ADDONS = ADDON_LIBRARY.filter((a) => a.party);
export function libraryAddon(id: string): LibraryAddon | undefined {
  return ADDON_LIBRARY.find((a) => a.id === id);
}
